import React, { Component } from 'react';
import { func, bool } from 'prop-types';
import { connect } from 'react-redux';
import {
  updateSurveySchemaPartOptionsAction,
  addSchemaPartItemAction,
  removeSchemaPartItemAction,
  updateSchemaPartItemOrderAction
} from '../../../../actions';
import {
  questionTypeValues,
  schemaPartItemTypeValues,
  SurveySchemaPartEditorType,
  staticTextTypeValues, surveyPartTypeValues
} from '../../../../constants/types';
import styles from './surveyPartItems.module.css';
import { ItemHeader, ItemBody } from '../SurveyPartItem';
import { getTranslation, withLang, langType } from '../../../../i18n';
import AddElementMenu from '../AddElementMenu';
import { LocalizedInput } from '../../../LocalizedInput';
import SurveyPartItemDragAndDrop from '../SurveyPartItemDragAndDrop';

class SurveyPartItems extends Component {
  constructor(props) {
    super(props);

    this.onToggleShowElement = this.onToggleShowElement.bind(this);
    this.onRemoveItem = this.onRemoveItem.bind(this);
    this.getPreviewTitle = this.getPreviewTitle.bind(this);
    this.addSchemaPartItem = this.addSchemaPartItem.bind(this);
    this.buildItem = this.buildItem.bind(this);
    this.nextFreeQuestionCode = this.nextFreeQuestionCode.bind(this);
    this.renderTitleElement = this.renderTitleElement.bind(this);
    this.onLocalizedInputChange = this.onLocalizedInputChange.bind(this);
    this.buildQuestion = this.buildQuestion.bind(this);
    this.buildStaticText = this.buildStaticText.bind(this);
    this.getOrder = this.getOrder.bind(this);
    this.buildSchemaPartItem = this.buildSchemaPartItem.bind(this);

    this.state = {
      titleElementOpen: true,
      itemElementsOpen: Array(props.surveySchemaPart.surveySchemaPartItems.length).fill(false)
    };
  }

  onToggleShowElement(key) {
    return () => {
      const titleElementStateAfter = key === undefined && !this.state.titleElementOpen;
      this.setState((oldState) => ({
        titleElementOpen: titleElementStateAfter,
        itemElementsOpen: oldState.itemElementsOpen.map((item, idx) => idx === key && !item)
      }));
    };
  }

  onRemoveItem(index) {
    return () => {
      this.props.dispatchRemoveItem(index);
      this.setState((oldState) => ({
        itemElementsOpen: oldState.itemElementsOpen
          .slice(0, oldState.itemElementsOpen.length - 1).map(() => false)
      }));
    };
  }

  onLocalizedInputChange(lang) {
    return (event) =>
      this.props.dispatchUpdate({
        [event.target.name]: { [lang]: event.target.value }
      });
  }

  static getResponseOptions(amount = 2) {
    return Array(amount).fill({}).map((ro, idx) => ({ value: idx + 1, text: {} }));
  }

  getOrder() {
    return this.props.surveySchemaPart.surveySchemaPartItems.length + 1;
  }

  getPreviewTitle(content) {
    return getTranslation(content, this.props.currentLanguage);
  }

  addSchemaPartItem(questionType) {
    const item = this.buildItem(questionType);
    if (item) {
      this.props.dispatchAddItem(item);
      this.setState((oldState) => ({
        titleElementOpen: false,
        itemElementsOpen: [...oldState.itemElementsOpen.map(() => false), true]
      }));
    }
  }

  buildSchemaPartItem(schemaPartItem, defaults, values) {
    const typeObject = Object.assign(defaults, values);
    if (!typeObject.type || !schemaPartItem) {
      return null;
    }
    return {
      type: schemaPartItem,
      order: this.getOrder(),
      question: schemaPartItemTypeValues.QUESTION === schemaPartItem ? typeObject : undefined,
      staticText: schemaPartItemTypeValues.STATIC_TEXT === schemaPartItem ? typeObject : undefined
    };
  }

  buildQuestion(question = {}) {
    const defaultValues = {
      required: false,
      code: this.nextFreeQuestionCode(),
      text: {},
      responseOptions: []
    };
    return this.buildSchemaPartItem(schemaPartItemTypeValues.QUESTION,
      defaultValues,
      question);
  }

  buildStaticText(staticText = {}) {
    const defaultValues = {
      text: {}
    };
    return this.buildSchemaPartItem(
      schemaPartItemTypeValues.STATIC_TEXT,
      defaultValues,
      staticText
    );
  }

  buildItem(type) {
    switch (type) {
      case questionTypeValues.TEXT_FIELD: {
        return this.buildQuestion({ type });
      }
      case questionTypeValues.LIKERT: {
        return {
          type: schemaPartItemTypeValues.QUESTION_GROUP,
          order: this.getOrder(),
          questionGroup: {
            // Default on creating likert 5
            responseOptions: [],
            questions: [{
              type: questionTypeValues.LIKERT,
              required: false,
              code: this.nextFreeQuestionCode(),
              text: {}
            }]
          }
        };
      }
      case questionTypeValues.DROPDOWN:
      case questionTypeValues.RADIO_BUTTON_GROUP:
      case questionTypeValues.CHECKBOX_GROUP: {
        return this.buildQuestion({
          type,
          responseOptions: SurveyPartItems.getResponseOptions()
        });
      }
      case staticTextTypeValues.HEADING:
      case staticTextTypeValues.INSTRUCTION_TEXT: {
        return this.buildStaticText({ type });
      }
      default:
        return null;
    }
  }

  nextFreeQuestionCode() {
    const getIndex = (code) => code.match(/(.*)_(\d*)$/) || [];
    const maxIndex = (curr, code) => Math.max(curr, Number(getIndex(code)[2]) || 0);
    const lastUsedQuestionCodeIndex = () =>
      this.props.surveySchemaPart.surveySchemaPartItems.reduce((max, item) => {
        switch (item.type) {
          case schemaPartItemTypeValues.QUESTION:
            return maxIndex(max, item.question.code);
          case schemaPartItemTypeValues.QUESTION_GROUP:
            return item.questionGroup.questions
              .reduce((localMax, q) => maxIndex(localMax, q.code), max);
          default:
            return max;
        }
      }, 0);

    const index = lastUsedQuestionCodeIndex() + 1;
    return `${this.props.surveySchemaPart.code}_${index}`;
  }

  renderTitleElement() {
    const { surveySchemaPart } = this.props;
    const translationBase = surveySchemaPart.type === surveyPartTypeValues.SURVEY_SCHEMA_PART
      ? 'surveySchemaParts'
      : 'extraSurveyParts';
    return (
      <div>
        <ItemHeader
          isOpen={this.state.titleElementOpen}
          titleTranslationKey={`${translationBase}.partTitle`}
          preview={this.getPreviewTitle(this.props.surveySchemaPart.title)}
          toggleOpen={this.onToggleShowElement()}
        />
        <ItemBody isOpen={this.state.titleElementOpen}>
          <LocalizedInput
            name="title"
            values={surveySchemaPart.title}
            onInputChange={this.onLocalizedInputChange}
            placeholderTranslationNamespace="inLanguage"
            maxLength={255}
            dataCyPrefix="title-input"
          />
        </ItemBody>
      </div>
    );
  }

  render() {
    return (
      <div className={styles.elementsContainer}>
        {this.renderTitleElement()}
        <SurveyPartItemDragAndDrop
          schemaPartItems={this.props.surveySchemaPart.surveySchemaPartItems}
          nextFreeQuestionCode={this.nextFreeQuestionCode}
          updateSchemaPartItemOrder={this.props.dispatchUpdateSchemaPartItemOrder}
          onRemoveSchemaPartItem={this.onRemoveItem}
          itemElementsOpen={this.state.itemElementsOpen}
          onToggleShowElement={this.onToggleShowElement}
          lockedFromEditing={this.props.lockedFromEditing}
        />
        {!this.props.lockedFromEditing && <AddElementMenu addElement={this.addSchemaPartItem} />}
      </div>
    );
  }
}

SurveyPartItems.propTypes = {
  surveySchemaPart: SurveySchemaPartEditorType.isRequired,
  dispatchUpdate: func.isRequired,
  dispatchUpdateSchemaPartItemOrder: func.isRequired,
  dispatchAddItem: func.isRequired,
  dispatchRemoveItem: func.isRequired,
  lockedFromEditing: bool.isRequired,
  ...langType
};

const mapStateToProps = ({ surveySchemaPart }) => ({
  surveySchemaPart: surveySchemaPart.surveySchemaPartOptions
});

const mapDispatchToProps = (dispatch) => ({
  dispatchUpdate: (options) =>
    dispatch(updateSurveySchemaPartOptionsAction(options)),
  dispatchUpdateSchemaPartItemOrder: (schemaPartItems) =>
    dispatch(updateSchemaPartItemOrderAction(schemaPartItems)),
  dispatchAddItem: (item) =>
    dispatch(addSchemaPartItemAction(item)),
  dispatchRemoveItem: (index) =>
    dispatch(removeSchemaPartItemAction(index))
});

export default withLang(connect(mapStateToProps, mapDispatchToProps)(SurveyPartItems));
